// src/pages/Dashboard.jsx - Enhanced with rate limit handling
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { postsAPI, contentAPI } from '../services/api';
import PostCard from '../components/dashboard/PostCard';
import StatusConsole from '../components/dashboard/StatusConsole';
import ContentSelector from '../components/dashboard/ContentSelector';
import GenerationProgress from '../components/dashboard/GenerationProgress';
import ContentSourcesModal from '../components/dashboard/ContentSourcesModal';
import {
    PlusIcon,
    EyeIcon,
    SparklesIcon,
    DocumentTextIcon,
    ArrowPathIcon,
    BeakerIcon,
    ClockIcon,
    ExclamationTriangleIcon,
    InformationCircleIcon,
    Cog6ToothIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { OnboardingBanner } from '../components/onboarding/OnboardingBanner';
import  OnboardingTutorial from '../components/onboarding/OnboardingTutorial';
import { useOnboarding } from '../contexts/OnboardingProvider';
import {AlertTriangle} from "lucide-react";
import { useNavigate } from 'react-router-dom';

function Dashboard() {
    const { showTutorial, setShowTutorial, markOnboardingComplete } = useOnboarding();
    const [showContentSelector, setShowContentSelector] = useState(false);
    const [showContentSourcesModal, setShowContentSourcesModal] = useState(false);
    const [lastRefreshTime, setLastRefreshTime] = useState(null);
    const [refreshCount, setRefreshCount] = useState(0);
    const { user } = useAuth();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [showProgress, setShowProgress] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [generationStartTime, setGenerationStartTime] = useState(null);

    const [hasOpenAIKey, setHasOpenAIKey] = useState(null); // null = checking, true = has key, false = missing

    const [recentPostsPage, setRecentPostsPage] = useState(1);
    const [recentPostsPerPage, setRecentPostsPerPage] = useState(2);
    const [recentPostsFilter, setRecentPostsFilter] = useState('all');

    // Check if content was recently refreshed (within last 5 minutes)
    const isRecentlyRefreshed = lastRefreshTime &&
        (Date.now() - lastRefreshTime) < 5 * 60 * 1000;

    // Reset refresh count every hour
    useEffect(() => {
        const hourlyReset = setInterval(() => {
            setRefreshCount(0);
        }, 60 * 60 * 1000); // 1 hour

        return () => clearInterval(hourlyReset);
    }, []);


    const handleGeneratePost = () => {
        // Check if OpenAI API key is configured
        if (hasOpenAIKey === false) {
            toast.error(
                'OpenAI API key not configured! Please add your API key in Settings.',
                {
                    duration: 4000,
                    icon: '🔑',
                }
            );
            navigate('/settings#credentials');
            return;
        }

        // Check if we have cached content
        const cachedContentList = cachedContent?.data?.data || [];

        if (!cachedContentList || cachedContentList.length === 0) {
            // Show message and navigate to content sources
            toast.error(
                'No content sources found! Please add content sources first to generate posts.',
                {
                    duration: 4000,
                    icon: '📚',
                }
            );

            // Navigate to content sources tab
            navigate('/settings#content-sources');
            return;
        }

        // If we have both API key and content, proceed with normal generation
        generateMutation.mutate();
    };

    const { data: posts, isLoading, error } = useQuery('posts', postsAPI.list, {
        retry: 1,
        refetchInterval: (data) => {
            // Poll every 2 seconds during generation, otherwise use the existing logic
            if (isGenerating) return 2000;

            const postsArray = data?.data?.data || [];
            const hasGeneratingPosts = postsArray.some(post =>
                ['queued', 'generating', 'posting'].includes(post.status)
            );
            return hasGeneratingPosts ? 3000 : 30000;
        },
        refetchIntervalInBackground: true,
        onError: (error) => {
            console.error('Posts query error:', error);
            if (error.response?.status === 401) {
                toast.error('Please log in again');
            } else {
                toast.error('Failed to load posts');
            }
        }
    });

    const { data: cachedContent } = useQuery(
        'cachedContent',
        () => contentAPI.getCached({ limit: 10 }),
        {
            refetchInterval: 60000, // Refresh every minute
            retry: 1,
            onError: (error) => {
                console.error('Cached content error:', error);
            }
        }
    );

    const { data: contentSuggestions } = useQuery(
        'contentSuggestions',
        () => postsAPI.getSuggestions(),
        {
            refetchInterval: 5 * 60 * 1000, // Refresh every 5 minutes
            retry: 1,
            onError: (error) => {
                console.error('Content suggestions error:', error);
            }
        }
    );

    // Check if user has OpenAI API key configured
    const { data: credentials } = useQuery(
        'userCredentials',
        async () => {
            const response = await fetch('/api/settings/credentials', {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`
                }
            });
            if (!response.ok) throw new Error('Failed to fetch credentials');
            return response.json();
        },
        {
            onSuccess: (data) => {
                const hasKey = !!(data?.data?.OPENAI_API_KEY);
                setHasOpenAIKey(hasKey);
            },
            onError: () => {
                setHasOpenAIKey(false);
            },
            retry: 1,
            refetchInterval: 5 * 60 * 1000 // Check every 5 minutes
        }
    );

    const generateMutation = useMutation(postsAPI.create, {
        onSuccess: (data) => {
            queryClient.invalidateQueries('posts');
            queryClient.invalidateQueries('contentSuggestions');
            toast.success('Post generation started! 🚀', {
                duration: 3000,
            });

            // Start progress tracking
            setIsGenerating(true);
            setShowProgress(true);
            setGenerationStartTime(Date.now());
        },
        onError: (error) => {
            console.error('Generate mutation error:', error);

            // Stop progress on error
            setIsGenerating(false);
            setShowProgress(false);
            setGenerationStartTime(null);

            if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
                toast.error('Generation is taking longer than expected. Your post may still be processing - check back in a minute!', {
                    duration: 8000,
                    icon: '⏱️'
                });
                setTimeout(() => {
                    queryClient.invalidateQueries('posts');
                }, 5000);
            } else {
                toast.error(error.response?.data?.message || 'Generation failed');
            }
        },
    });

    const generateWithContentMutation = useMutation(
        (contentId) => postsAPI.createWithContent(contentId),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('posts');
                queryClient.invalidateQueries('contentSuggestions');
                toast.success('Post generation started with selected content! 🚀', {
                    duration: 3000,
                });

                // Start progress tracking
                setIsGenerating(true);
                setShowProgress(true);
                setGenerationStartTime(Date.now());
                setShowContentSelector(false);
            },
            onError: (error) => {
                console.error('Generate with content mutation error:', error);

                // Stop progress on error
                setIsGenerating(false);
                setShowProgress(false);
                setGenerationStartTime(null);

                if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
                    toast.error('Generation is taking longer than expected. Your post may still be processing - check back in a minute!', {
                        duration: 8000,
                        icon: '⏱️'
                    });
                    setTimeout(() => {
                        queryClient.invalidateQueries('posts');
                    }, 5000);
                    setShowContentSelector(false);
                } else {
                    toast.error(error.response?.data?.message || 'Generation failed');
                }
            },
        }
    );

    const generateBatchMutation = useMutation(
        ({ count, useRandomContent }) => postsAPI.createBatch(count, useRandomContent),
        {
            onSuccess: (data) => {
                queryClient.invalidateQueries('posts');
                queryClient.invalidateQueries('contentSuggestions');
                toast.success(`Batch generation complete: ${data.data.success.length}/${data.data.total} successful`);
                // setShowConsole(true);
            },
            onError: (error) => {
                toast.error(error.message || 'Batch generation failed');
            },
        }
    );

    const refreshContentMutation = useMutation(
        () => contentAPI.refresh([]),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('cachedContent');
                setLastRefreshTime(Date.now());
                setRefreshCount(prev => prev + 1);
                toast.success('Content refresh started in background');
            },
            onError: (error) => {
                const errorMessage = error.response?.data?.message || error.message;

                if (error.isNoSourcesConfigured) {
                    // Show content sources modal instead of just an error
                    toast.error('No content sources configured');

                    setTimeout(() => {
                        setShowContentSourcesModal(true);
                    }, 500);

                } else if (error.response?.status === 429) {
                    // Rate limit exceeded
                    const rateLimitInfo = error.response?.data?.rateLimitInfo;
                    const retryAfter = error.response?.data?.retryAfter;

                    toast.error(
                        `Rate limit exceeded. Try again in ${Math.ceil(retryAfter / 60)} minutes.`,
                        { duration: 8000 }
                    );

                    // Show additional info
                    setTimeout(() => {
                        toast.custom((t) => (
                            <div className={`${
                                t.visible ? 'animate-enter' : 'animate-leave'
                            } max-w-md w-full bg-yellow-50 border border-yellow-200 shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`}>
                                <div className="flex-1 w-0 p-4">
                                    <div className="flex items-start">
                                        <div className="flex-shrink-0">
                                            <InformationCircleIcon className="h-6 w-6 text-yellow-400" />
                                        </div>
                                        <div className="ml-3 flex-1">
                                            <p className="text-sm font-medium text-yellow-800">
                                                Content Refresh Rate Limit
                                            </p>
                                            <p className="mt-1 text-sm text-yellow-700">
                                                {rateLimitInfo ?
                                                    `Limit: ${rateLimitInfo.max} requests per hour` :
                                                    'You\'ve made too many refresh requests'
                                                }
                                            </p>
                                            <p className="mt-1 text-xs text-yellow-600">
                                                Content is automatically refreshed in the background every 6 hours
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex border-l border-yellow-200">
                                    <button
                                        onClick={() => toast.dismiss(t.id)}
                                        className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-yellow-600 hover:text-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        ), { duration: 15000 });
                    }, 1000);
                } else {
                    toast.error(`Failed to refresh content: ${errorMessage}`);
                }
            }
        }
    );

    const handleGenerate = () => {
        // Check if OpenAI API key is configured
        if (hasOpenAIKey === false) {
            toast.error(
                'OpenAI API key not configured! Please add your API key in Settings.',
                {
                    duration: 4000,
                    icon: '🔑',
                }
            );
            navigate('/settings#credentials');
            return;
        }

        // Check if we have cached content (same logic as handleGeneratePost)
        const cachedContentList = cachedContent?.data?.data || [];

        if (!cachedContentList || cachedContentList.length === 0) {
            // Show message and navigate to content sources
            toast.error(
                'No content sources found! Please add content sources first to generate posts.',
                {
                    duration: 4000,
                    icon: '📚',
                }
            );

            // Navigate to content sources tab
            navigate('/settings#content-sources');
            return;
        }

        // If we have content, proceed with generation
        generateMutation.mutate();
    };

    const handleGenerateWithContent = (contentId) => {
        generateWithContentMutation.mutate(contentId);
    };

    const handleBatchGenerate = () => {
        const count = prompt('How many posts to generate? (1-5)', '3');
        if (count && parseInt(count) >= 1 && parseInt(count) <= 5) {
            generateBatchMutation.mutate({
                count: parseInt(count),
                useRandomContent: true
            });
        }
    };

    const handleRefreshContent = () => {
        // Show warning if refreshed recently
        if (isRecentlyRefreshed) {
            if (!window.confirm(
                'Content was refreshed recently. Content is automatically updated every 6 hours. ' +
                'Are you sure you want to refresh again?'
            )) {
                return;
            }
        }

        // Show warning if approaching rate limit
        if (refreshCount >= 15) {
            if (!window.confirm(
                `You've refreshed content ${refreshCount} times this hour. ` +
                'The rate limit is 20 per hour in development (100 in production). Continue?'
            )) {
                return;
            }
        }

        refreshContentMutation.mutate();
    };

    const handleContentSourcesConfigured = (sources) => {
        // console.log('Content sources configured:', sources);
        queryClient.invalidateQueries('cachedContent');
        setShowContentSourcesModal(false);
    };

    // Reset page when filter changes
    useEffect(() => {
        setRecentPostsPage(1);
    }, [recentPostsFilter]);

    // Monitor posts to detect when generation completes
    useEffect(() => {
        if (isGenerating && generationStartTime && posts?.data?.data) {
            const recentPosts = posts.data.data.filter(post => {
                const postTime = new Date(post.createdAt).getTime();
                return postTime >= generationStartTime - 5000; // 5 second buffer
            });

            if (recentPosts.length > 0) {
                const latestPost = recentPosts[0];
                // Complete when post is created and ready for review
                if (['queued', 'approved', 'posted', 'failed'].includes(latestPost.status)) {
                    setIsGenerating(false);

                    // Keep progress bar visible for a moment before hiding
                    setTimeout(() => {
                        setShowProgress(false);
                        setGenerationStartTime(null);

                        if (latestPost.status !== 'failed') {
                            toast.success(`Post "${latestPost.title}" is ready for review!`, {
                                duration: 5000,
                                icon: '✅'
                            });

                            // Smooth scroll to the new post
                            setTimeout(() => {
                                const postsSection = document.querySelector('[data-posts-grid]');
                                if (postsSection) {
                                    postsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                }
                            }, 500);
                        }
                    }, 2000); // Show completion for 2 seconds
                }
            }
        }
    }, [posts, isGenerating, generationStartTime]);

    // Auto-hide progress after max time (failsafe)
    useEffect(() => {
        if (isGenerating && generationStartTime) {
            const maxGenerationTime = 120000; // 2 minutes
            const timeoutId = setTimeout(() => {
                if (isGenerating) {
                    setIsGenerating(false);
                    setShowProgress(false);
                    setGenerationStartTime(null);
                    toast.error('Generation took longer than expected. Please check your posts manually.', {
                        duration: 6000,
                        icon: '⏰'
                    });
                }
            }, maxGenerationTime);

            return () => clearTimeout(timeoutId);
        }
    }, [isGenerating, generationStartTime]);


    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (error) {
        console.error('Dashboard error:', error);
        return (
            <div className="text-center py-12">
                <div className="text-red-600 mb-4">
                    Failed to load posts: {error.response?.data?.message || error.message}
                </div>
                <div className="text-sm text-gray-600 mb-4">
                    {error.response?.status === 401 ? 'Authentication required' :
                        error.response?.status === 403 ? 'Access denied' :
                            'Check console for details'}
                </div>
                <button
                    onClick={() => queryClient.invalidateQueries('posts')}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                    Retry
                </button>
            </div>
        );
    }

    const postsData = posts?.data?.data || posts?.data || [];
    const postsArray = Array.isArray(postsData) ? postsData : [];
    const cachedContentList = cachedContent?.data || [];
    const suggestions = contentSuggestions?.data || [];

    const pendingPosts = postsArray.filter(post => post?.status === 'queued');
    const approvedPosts = postsArray.filter(post => post?.status === 'approved');
    const publishedPosts = postsArray.filter(post => post?.status === 'posted');

    // Calculate refresh button state
    const refreshButtonDisabled = refreshContentMutation.isLoading ||
        (refreshCount >= 20); // Disable at rate limit

    // Recent Posts filtering and pagination
    const filteredRecentPosts = postsArray.filter(post => {
        if (recentPostsFilter === 'all') return true;
        return post.status === recentPostsFilter;
    });

    const totalRecentPages = Math.ceil(filteredRecentPosts.length / recentPostsPerPage);
    const recentPostsStartIndex = (recentPostsPage - 1) * recentPostsPerPage;
    const recentPostsEndIndex = recentPostsStartIndex + recentPostsPerPage;
    const currentRecentPosts = filteredRecentPosts.slice(recentPostsStartIndex, recentPostsEndIndex);



    return (
        <div className="space-y-6">
            <OnboardingBanner />
            <OnboardingTutorial
                isOpen={showTutorial}
                onClose={() => setShowTutorial(false)}
                onComplete={markOnboardingComplete}
            />

            {user?.isFlagged && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-4">
                    <div className="flex">
                        <AlertTriangle className="h-5 w-5 text-yellow-400" />
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-yellow-800">
                                Account Flagged
                            </h3>
                            <p className="mt-1 text-sm text-yellow-700">
                                Your account has been flagged for review. Some features may be limited.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Header */}
            <div className="sm:flex sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">
                        Welcome back, {user?.firstName || 'there'}! 👋
                    </h1>
                    <p className="mt-2 text-sm text-gray-700">
                        Generate AI-powered social media posts from your cached content
                    </p>
                </div>
                <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none flex space-x-2">
                    {/* Enhanced Refresh Button */}
                    <div className="relative group">
                        <button
                            onClick={handleRefreshContent}
                            disabled={refreshButtonDisabled}
                            className={`inline-flex items-center px-3 py-2 border text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed ${
                                refreshCount >= 15 ?
                                    'border-yellow-300 text-yellow-700 bg-yellow-50 hover:bg-yellow-100' :
                                    'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                            }`}
                            title={refreshButtonDisabled ?
                                'Rate limit reached' :
                                `Refresh cached content (${refreshCount}/20 this hour)`
                            }
                        >
                            <ArrowPathIcon className={`h-4 w-4 mr-2 ${refreshContentMutation.isLoading ? 'animate-spin' : ''}`} />
                            Refresh Content
                            {refreshCount >= 15 && (
                                <ExclamationTriangleIcon className="h-4 w-4 ml-1" />
                            )}
                        </button>

                        {/* Tooltip showing refresh info */}
                        {(isRecentlyRefreshed || refreshCount > 10) && (
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 text-xs text-white bg-gray-900 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                                {isRecentlyRefreshed && (
                                    <div>Last refreshed: {Math.floor((Date.now() - lastRefreshTime) / 1000 / 60)}m ago</div>
                                )}
                                <div>Requests this hour: {refreshCount}/20</div>
                                {refreshCount >= 15 && (
                                    <div className="text-yellow-300">Approaching rate limit!</div>
                                )}
                            </div>
                        )}
                    </div>

                    <button
                        onClick={handleGeneratePost}
                        disabled={generateMutation.isLoading || hasOpenAIKey === false}
                        className={`inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md transition-all duration-200 ${
                            generateMutation.isLoading || hasOpenAIKey === false
                                ? 'bg-gray-400 text-white cursor-not-allowed'
                                : 'text-white bg-blue-600 hover:bg-blue-700'
                        }`}
                        title={hasOpenAIKey === false ? 'OpenAI API key required' : ''}
                    >
                        {generateMutation.isLoading ? (
                            <>
                                <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Generating...
                            </>
                        ) : hasOpenAIKey === false ? (
                            <>
                                <ExclamationTriangleIcon className="h-4 w-4 mr-2" />
                                API Key Required
                            </>
                        ) : (
                            <>
                                <PlusIcon className="h-4 w-4 mr-2" />
                                Generate Post
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Rate Limit Info Banner (if approaching limit) */}
            {refreshCount >= 15 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-yellow-800">
                                Approaching Content Refresh Rate Limit
                            </h3>
                            <div className="mt-2 text-sm text-yellow-700">
                                <p>
                                    You've used {refreshCount}/20 content refresh requests this hour.
                                    Content is automatically refreshed every 6 hours in the background.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Quick Stats */}
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5">
                <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                                    <span className="text-white text-sm font-medium">📝</span>
                                </div>
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">Total Posts</dt>
                                    <dd className="text-lg font-medium text-gray-900">{postsArray.length}</dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <div className="w-8 h-8 bg-yellow-500 rounded-md flex items-center justify-center">
                                    <span className="text-white text-sm font-medium">⏳</span>
                                </div>
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">Pending</dt>
                                    <dd className="text-lg font-medium text-gray-900">{pendingPosts.length}</dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                                    <span className="text-white text-sm font-medium">✅</span>
                                </div>
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">Ready</dt>
                                    <dd className="text-lg font-medium text-gray-900">{approvedPosts.length}</dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <div className="w-8 h-8 bg-purple-500 rounded-md flex items-center justify-center">
                                    <span className="text-white text-sm font-medium">📊</span>
                                </div>
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">Published</dt>
                                    <dd className="text-lg font-medium text-gray-900">{publishedPosts.length}</dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <div className="w-8 h-8 bg-indigo-500 rounded-md flex items-center justify-center">
                                    <span className="text-white text-sm font-medium">📚</span>
                                </div>
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">Cached Content</dt>
                                    <dd className="text-lg font-medium text-gray-900">{cachedContentList.length}</dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Rest of the component remains the same... */}
            {/* Generation Options */}
            {/* Generation Options */}
            <div className="bg-white shadow rounded-lg p-6">
                <h2 className="text-lg font-medium text-gray-900 mb-4">Generate Content</h2>

                {/* API Key Warning Banner */}
                {hasOpenAIKey === false && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                            </div>
                            <div className="ml-3">
                                <h3 className="text-sm font-medium text-red-800">
                                    OpenAI API Key Required
                                </h3>
                                <div className="mt-2 text-sm text-red-700">
                                    <p>
                                        Content generation requires an OpenAI API key.
                                        <button
                                            onClick={() => navigate('/settings#credentials')}
                                            className="ml-1 font-medium underline hover:text-red-600"
                                        >
                                            Add your API key in Settings →
                                        </button>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <button
                        onClick={handleGenerate}
                        disabled={generateMutation.isLoading || hasOpenAIKey === false}
                        className={`flex flex-col items-center p-4 border-2 rounded-lg transition-colors ${
                            hasOpenAIKey === false
                                ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-50'
                                : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                        }`}
                    >
                        <img src="/images/Bob_only.svg" alt="PostMgr" className="h-8 w-8" />
                        <span className="font-medium text-gray-900">Random Content</span>
                        <span className="text-sm text-gray-500 text-center">
                {hasOpenAIKey === false ? 'Requires OpenAI API key' : 'AI picks content from your cache'}
            </span>
                    </button>

                    <button
                        onClick={() => setShowContentSelector(true)}
                        disabled={hasOpenAIKey === false}
                        className={`flex flex-col items-center p-4 border-2 rounded-lg transition-colors ${
                            hasOpenAIKey === false
                                ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-50'
                                : 'border-gray-200 hover:border-green-300 hover:bg-green-50'
                        }`}
                    >
                        <DocumentTextIcon className="h-8 w-8 text-green-600 mb-2" />
                        <span className="font-medium text-gray-900">Select Content</span>
                        <span className="text-sm text-gray-500 text-center">
                {hasOpenAIKey === false ? 'Requires OpenAI API key' : 'Choose specific content to post about'}
            </span>
                    </button>
                </div>
            </div>

            {/* Content Suggestions */}
            {suggestions.length > 0 && (
                <div className="bg-white shadow rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-medium text-gray-900">Content Suggestions</h2>
                        <span className="text-sm text-gray-500">Fresh content ready for posts</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {suggestions.slice(0, 6).map((content) => (
                            <div key={content._id} className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
                                <h3 className="font-medium text-gray-900 text-sm mb-2 line-clamp-2">
                                    {content.title}
                                </h3>
                                <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                                    {content.summary || content.content?.substring(0, 100)}...
                                </p>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center text-xs text-gray-500">
                                        <ClockIcon className="h-3 w-3 mr-1" />
                                        {new Date(content.metadata?.lastFetched).toLocaleDateString()}
                                    </div>
                                    <button
                                        onClick={() => handleGenerateWithContent(content._id)}
                                        disabled={generateWithContentMutation.isLoading}
                                        className={`flex flex-col items-center p-4 border-2 rounded-lg transition-all duration-200 ${
                                            generateWithContentMutation.isLoading
                                                ? 'border-gray-300 bg-gray-50 cursor-not-allowed opacity-50'
                                                : 'border-gray-200 hover:border-green-300 hover:bg-green-50'
                                        }`}
                                    >
                                        Generate
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Generation Progress */}
            {showProgress && (
                <GenerationProgress
                    isGenerating={isGenerating}
                    onComplete={(post) => {
                        setIsGenerating(false);
                        setTimeout(() => {
                            setShowProgress(false);
                            setGenerationStartTime(null);
                        }, 2000);
                    }}
                />
            )}

            {/* Recent Posts Header with Controls */}
            <div className="bg-white shadow rounded-lg p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                    <div className="flex items-center space-x-4">
                        <h2 className="text-lg font-medium text-gray-900">Recent Posts</h2>
                        <div className="text-sm text-gray-500">
                            Showing {currentRecentPosts.length} of {filteredRecentPosts.length} posts
                        </div>
                    </div>

                    <div className="flex items-center space-x-2">
                        {/* Filter */}
                        <select
                            value={recentPostsFilter}
                            onChange={(e) => setRecentPostsFilter(e.target.value)}
                            className="block pl-3 pr-10 py-2 text-sm border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md"
                        >
                            <option value="all">All Status</option>
                            <option value="queued">Queued</option>
                            <option value="approved">Approved</option>
                            <option value="posting">Posting</option>
                            <option value="posted">Posted</option>
                            <option value="failed">Failed</option>
                        </select>

                        {/* Content Selector Toggle */}
                        <button
                            onClick={() => setShowContentSelector(!showContentSelector)}
                            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                        >
                            <DocumentTextIcon className="h-4 w-4 mr-2" />
                            {showContentSelector ? 'Hide' : 'Show'} Content
                        </button>

                        {/* View All Posts Button */}
                        <button
                            onClick={() => navigate('/posts')}
                            className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                        >
                            <EyeIcon className="h-4 w-4 mr-2" />
                            View All Posts
                        </button>
                    </div>
                </div>

                {/* Posts Grid */}
                {filteredRecentPosts.length === 0 ? (
                    <div className="text-center py-8">
                        {recentPostsFilter === 'all' ? (
                            <>
                                <img src="/images/Bob_only.svg" alt="PostMgr" className="h-8 w-8 mx-auto mb-3" />
                                <h3 className="mt-2 text-sm font-medium text-gray-900">No posts yet</h3>
                                <p className="mt-1 text-sm text-gray-500">
                                    Get started by generating your first social media post.
                                </p>
                            </>
                        ) : (
                            <>
                                <h3 className="mt-2 text-sm font-medium text-gray-900">No {recentPostsFilter} posts found</h3>
                                <p className="mt-1 text-sm text-gray-500">
                                    Try selecting a different filter or generate more posts.
                                </p>
                            </>
                        )}
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2" data-posts-grid>
                            {currentRecentPosts.map((post) => (
                                <PostCard key={post._id} post={post} />
                            ))}
                        </div>

                        {/* Pagination Controls */}
                        <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                            <div className="flex items-center space-x-4">
                                {/* Show More/Less Toggle */}
                                {recentPostsPerPage === 2 && filteredRecentPosts.length > 2 && (
                                    <button
                                        onClick={() => {
                                            setRecentPostsPerPage(6);
                                            setRecentPostsPage(1);
                                        }}
                                        className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                    >
                                        <SparklesIcon className="h-4 w-4 mr-2" />
                                        Show More ({Math.min(6, filteredRecentPosts.length)} total)
                                    </button>
                                )}

                                {recentPostsPerPage === 6 && (
                                    <button
                                        onClick={() => {
                                            setRecentPostsPerPage(2);
                                            setRecentPostsPage(1);
                                        }}
                                        className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                    >
                                        Show Less
                                    </button>
                                )}
                            </div>

                            {/* Page Navigation */}
                            {totalRecentPages > 1 && (
                                <div className="flex items-center space-x-2">
                                    <button
                                        onClick={() => setRecentPostsPage(Math.max(1, recentPostsPage - 1))}
                                        disabled={recentPostsPage === 1}
                                        className="inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        ← Previous
                                    </button>

                                    <div className="flex items-center space-x-1">
                                        {Array.from({ length: Math.min(totalRecentPages, 5) }, (_, i) => {
                                            const pageNum = i + 1;
                                            return (
                                                <button
                                                    key={pageNum}
                                                    onClick={() => setRecentPostsPage(pageNum)}
                                                    className={`inline-flex items-center px-3 py-1 text-sm leading-4 font-medium rounded-md ${
                                                        recentPostsPage === pageNum
                                                            ? 'bg-blue-600 text-white'
                                                            : 'border border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                                                    }`}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        })}
                                        {totalRecentPages > 5 && (
                                            <>
                                                {totalRecentPages > 6 && <span className="text-gray-500">...</span>}
                                                <button
                                                    onClick={() => setRecentPostsPage(totalRecentPages)}
                                                    className={`inline-flex items-center px-3 py-1 text-sm leading-4 font-medium rounded-md ${
                                                        recentPostsPage === totalRecentPages
                                                            ? 'bg-blue-600 text-white'
                                                            : 'border border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                                                    }`}
                                                >
                                                    {totalRecentPages}
                                                </button>
                                            </>
                                        )}
                                    </div>

                                    <button
                                        onClick={() => setRecentPostsPage(Math.min(totalRecentPages, recentPostsPage + 1))}
                                        disabled={recentPostsPage === totalRecentPages}
                                        className="inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Next →
                                    </button>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>

            {/* Content Selector Modal */}
            {showContentSelector && (
                <ContentSelector
                    onSelectContent={handleGenerateWithContent}
                    onClose={() => setShowContentSelector(false)}
                    isGenerating={generateWithContentMutation.isLoading}
                />
            )}



        </div>
    );
}

export default Dashboard;